import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Lightning, ArrowDown, Plus, Trash, GearSix, ArrowRight,
  FunnelSimple, DotsSixVertical, Copy, Timer, WebhookLogo,
  GitBranch, CaretDown, Eye, Code
} from '@phosphor-icons/react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TRIGGER_ICONS = { entity_status_change: '🔄', entity_created: '✨', payment_received: '💰', dossier_validated: '✅', task_completed: '☑️' };
const ACTION_COLORS = {
  create_task: '#3B82F6', send_notification: '#F59E0B', update_field: '#8B5CF6',
  create_activity: '#10B981', send_email: '#EC4899', send_sms: '#06B6D4',
  delay: '#78716C', webhook_http: '#6366F1', condition_branch: '#F97316',
};
const ACTION_NODE_ICONS = {
  delay: Timer, webhook_http: Code, condition_branch: GitBranch,
};
const DELAY_UNITS = [
  { value: 'seconds', label: 'Secondes' },
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Heures' },
];
const HTTP_METHODS = ['GET', 'POST', 'PUT', 'DELETE'];

/* ===== Variable Autocomplete Input ===== */
function VariableInput({ value, onChange, placeholder, schema, className, ...props }) {
  const [showVars, setShowVars] = useState(false);
  const inputRef = useRef(null);
  const variables = schema?.variables || [];

  const insertVariable = (varKey) => {
    const input = inputRef.current;
    if (!input) return;
    const start = input.selectionStart || (value || '').length;
    const before = (value || '').slice(0, start);
    const after = (value || '').slice(start);
    onChange(before + `{${varKey}}` + after);
    setShowVars(false);
    setTimeout(() => input.focus(), 50);
  };

  const resolvedPreview = useMemo(() => {
    if (!value) return null;
    const vars = value.match(/\{([^}]+)\}/g);
    if (!vars) return null;
    return vars.map(v => {
      const key = v.replace(/[{}]/g, '');
      const found = variables.find(vr => vr.key === key);
      return found ? `${v} = ${found.label}` : null;
    }).filter(Boolean).join(', ');
  }, [value, variables]);

  return (
    <div className="relative">
      <div className="flex gap-1">
        <Input
          ref={inputRef}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className={className}
          {...props}
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setShowVars(!showVars)}
                className="flex-shrink-0 w-6 h-6 rounded bg-[#2D5A4C]/10 hover:bg-[#2D5A4C]/20 flex items-center justify-center transition-colors"
                data-testid="var-insert-btn"
              >
                <Code size={10} className="text-[#2D5A4C]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top"><p className="text-[10px]">Inserer une variable</p></TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {resolvedPreview && (
        <p className="text-[8px] text-[#2D5A4C] mt-0.5 font-mono truncate">{resolvedPreview}</p>
      )}
      {showVars && (
        <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-[#E8E6E1] rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="variable-dropdown">
          <div className="p-1.5 border-b border-[#F0EFEB]">
            <p className="text-[9px] text-[#5E6D66] font-bold uppercase tracking-wider">Variables dynamiques</p>
          </div>
          {variables.map(v => (
            <button
              key={v.key}
              onClick={() => insertVariable(v.key)}
              className="w-full text-left px-2.5 py-1.5 hover:bg-[#F9F8F5] flex items-center justify-between transition-colors"
            >
              <span className="text-[10px] font-mono text-[#2D5A4C]">{`{${v.key}}`}</span>
              <span className="text-[9px] text-[#5E6D66]">{v.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ===== Sortable Action Node ===== */
function SortableAction({ id, action, index, schema, onUpdate, onRemove, onDuplicate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' };
  const color = ACTION_COLORS[action.type] || '#6B7280';
  const NodeIcon = ACTION_NODE_ICONS[action.type];

  const renderFieldsForType = () => {
    if (!action.type || !schema?.actions?.[action.type]?.fields) return null;
    const fields = schema.actions[action.type].fields;

    if (action.type === 'delay') {
      return (
        <div className="px-3 pb-3 pt-1 flex gap-2">
          <div className="flex-1">
            <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase tracking-wider">Duree</label>
            <Input value={action.config?.duration || ''} onChange={e => onUpdate(index, 'duration', e.target.value)}
              placeholder="10" type="number" className="h-7 text-xs" data-testid={`builder-action-delay-duration-${index}`} />
          </div>
          <div className="w-28">
            <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase tracking-wider">Unite</label>
            <Select value={action.config?.unit || 'minutes'} onValueChange={v => onUpdate(index, 'unit', v)}>
              <SelectTrigger className="h-7 text-xs" data-testid={`builder-action-delay-unit-${index}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {DELAY_UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    if (action.type === 'webhook_http') {
      return (
        <div className="px-3 pb-3 pt-1 space-y-2">
          <div className="flex gap-2">
            <div className="w-24">
              <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase tracking-wider">Methode</label>
              <Select value={action.config?.method || 'POST'} onValueChange={v => onUpdate(index, 'method', v)}>
                <SelectTrigger className="h-7 text-xs" data-testid={`builder-action-webhook-method-${index}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HTTP_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase tracking-wider">URL</label>
              <VariableInput value={action.config?.url || ''} onChange={v => onUpdate(index, 'url', v)}
                placeholder="https://..." schema={schema} className="h-7 text-xs" data-testid={`builder-action-webhook-url-${index}`} />
            </div>
          </div>
          <div>
            <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase tracking-wider">Corps (JSON)</label>
            <VariableInput value={action.config?.body || ''} onChange={v => onUpdate(index, 'body', v)}
              placeholder='{"key": "{reference}"}' schema={schema} className="h-7 text-xs font-mono" data-testid={`builder-action-webhook-body-${index}`} />
          </div>
        </div>
      );
    }

    if (action.type === 'condition_branch') {
      return (
        <div className="px-3 pb-3 pt-1 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase tracking-wider">Champ</label>
              <VariableInput value={action.config?.condition_field || ''} onChange={v => onUpdate(index, 'condition_field', v)}
                placeholder="statut" schema={schema} className="h-7 text-xs" data-testid={`builder-branch-field-${index}`} />
            </div>
            <div>
              <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase tracking-wider">Operateur</label>
              <Select value={action.config?.condition_operator || 'equals'} onValueChange={v => onUpdate(index, 'condition_operator', v)}>
                <SelectTrigger className="h-7 text-xs" data-testid={`builder-branch-op-${index}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {schema?.conditions && Object.entries(schema.conditions).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase tracking-wider">Valeur</label>
              <VariableInput value={action.config?.condition_value || ''} onChange={v => onUpdate(index, 'condition_value', v)}
                placeholder="valide" schema={schema} className="h-7 text-xs" data-testid={`builder-branch-val-${index}`} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-emerald-50 rounded p-2">
              <label className="text-[9px] text-emerald-700 mb-0.5 block font-bold uppercase">Si Vrai</label>
              <Input value={action.config?.true_action || ''} onChange={e => onUpdate(index, 'true_action', e.target.value)}
                placeholder="continuer" className="h-6 text-[10px]" data-testid={`builder-branch-true-${index}`} />
            </div>
            <div className="bg-red-50 rounded p-2">
              <label className="text-[9px] text-red-600 mb-0.5 block font-bold uppercase">Si Faux</label>
              <Input value={action.config?.false_action || ''} onChange={e => onUpdate(index, 'false_action', e.target.value)}
                placeholder="arreter" className="h-6 text-[10px]" data-testid={`builder-branch-false-${index}`} />
            </div>
          </div>
        </div>
      );
    }

    // Default: generic fields with variable support
    return (
      <div className="px-3 pb-3 pt-1 grid grid-cols-2 gap-2">
        {fields.map(f => (
          <div key={f}>
            <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase tracking-wider">{f}</label>
            <VariableInput value={action.config?.[f] || ''} onChange={v => onUpdate(index, f, v)}
              placeholder={`{variable}`} schema={schema} className="h-7 text-xs" data-testid={`builder-action-field-${index}-${f}`} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div ref={setNodeRef} style={style} className="group">
      <div className={`border rounded-lg overflow-hidden transition-shadow ${isDragging ? 'shadow-lg' : 'shadow-sm'}`} style={{ borderColor: `${color}30` }}>
        <div className="flex items-center gap-2 px-3 py-2" style={{ background: `${color}08` }}>
          <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 rounded hover:bg-black/5">
            <DotsSixVertical size={14} className="text-[#5E6D66]" />
          </button>
          {NodeIcon ? (
            <NodeIcon size={14} style={{ color }} weight="bold" />
          ) : (
            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
          )}
          <Select value={action.type} onValueChange={v => onUpdate(index, 'type', v)}>
            <SelectTrigger className="h-7 text-xs border-0 bg-transparent shadow-none pl-0 flex-1" data-testid={`builder-action-type-${index}`}>
              <SelectValue placeholder="Choisir une action..." />
            </SelectTrigger>
            <SelectContent>
              {schema?.action_categories && Object.entries(schema.action_categories).map(([catKey, catLabel]) => {
                const catActions = Object.entries(schema.actions || {}).filter(([, v]) => v.category === catKey);
                if (catActions.length === 0) return null;
                return (
                  <React.Fragment key={catKey}>
                    <div className="px-2 py-1 text-[9px] font-bold text-[#5E6D66] uppercase tracking-wider bg-[#F9F8F5]">{catLabel}</div>
                    {catActions.map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </React.Fragment>
                );
              })}
            </SelectContent>
          </Select>
          <button onClick={() => onDuplicate(index)} className="p-1 rounded hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity" title="Dupliquer">
            <Copy size={12} className="text-blue-400" />
          </button>
          <button onClick={() => onRemove(index)} className="p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash size={12} className="text-red-400" />
          </button>
        </div>
        {renderFieldsForType()}
      </div>
    </div>
  );
}

/* ===== Condition Group ===== */
function ConditionGroup({ group, groupIndex, operators, schema, onUpdateRule, onRemoveRule, onAddRule, onChangeLogic, onRemoveGroup, onDuplicate }) {
  return (
    <div className="border border-[#FF8E00]/20 rounded-lg p-3 bg-[#FF8E00]/3" data-testid={`condition-group-${groupIndex}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FunnelSimple size={12} className="text-[#FF8E00]" />
          <span className="text-[10px] font-bold text-[#FF8E00] uppercase">Groupe {groupIndex + 1}</span>
          <Select value={group.logic || 'and'} onValueChange={v => onChangeLogic(groupIndex, v)}>
            <SelectTrigger className="h-6 text-[10px] w-16 border-[#FF8E00]/30" data-testid={`group-logic-${groupIndex}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="and">ET</SelectItem>
              <SelectItem value="or">OU</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onAddRule(groupIndex)} className="text-[10px] text-[#FF8E00] hover:text-[#D47A62] flex items-center gap-0.5">
            <Plus size={10} /> Regle
          </button>
          <button onClick={() => onDuplicate(groupIndex)} className="text-[10px] text-blue-500 hover:text-blue-700 ml-1" title="Dupliquer le groupe">
            <Copy size={10} />
          </button>
          <button onClick={() => onRemoveGroup(groupIndex)} className="text-[10px] text-red-400 hover:text-red-600 ml-1">
            <Trash size={10} />
          </button>
        </div>
      </div>
      {(group.rules || []).map((rule, ri) => (
        <div key={ri} className="flex items-center gap-1.5 mb-1.5" data-testid={`rule-${groupIndex}-${ri}`}>
          <VariableInput value={rule.field || ''} onChange={v => onUpdateRule(groupIndex, ri, 'field', v)}
            placeholder="champ" schema={schema} className="h-6 text-[10px] flex-1" />
          <Select value={rule.operator || 'equals'} onValueChange={v => onUpdateRule(groupIndex, ri, 'operator', v)}>
            <SelectTrigger className="h-6 text-[10px] w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              {operators && Object.entries(operators).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <VariableInput value={rule.value || ''} onChange={v => onUpdateRule(groupIndex, ri, 'value', v)}
            placeholder="valeur" schema={schema} className="h-6 text-[10px] flex-1" />
          <button onClick={() => onRemoveRule(groupIndex, ri)} className="p-0.5 hover:bg-red-50 rounded">
            <Trash size={10} className="text-red-400" />
          </button>
        </div>
      ))}
      {(group.rules || []).length === 0 && (
        <p className="text-[9px] text-[#5E6D66] italic text-center py-1">Aucune regle (cliquer + Regle)</p>
      )}
    </div>
  );
}

/* ===== Mini Flow Preview ===== */
function FlowPreview({ triggerType, triggerEntity, conditionGroups, actions, schema }) {
  const triggerLabel = schema?.triggers?.[triggerType]?.label || triggerType || '?';
  const entityLabel = triggerEntity ? triggerEntity.charAt(0).toUpperCase() + triggerEntity.slice(1) : '?';
  const condCount = conditionGroups.reduce((sum, g) => sum + (g.rules || []).filter(r => r.field).length, 0);

  return (
    <div className="bg-[#1A2421] rounded-xl p-4 mb-4 overflow-x-auto" data-testid="flow-preview">
      <div className="flex items-center gap-0 min-w-max">
        {/* Trigger */}
        <div className="flex items-center gap-2 bg-[#2D5A4C] rounded-lg px-3 py-2 flex-shrink-0">
          <Lightning size={14} className="text-emerald-300" weight="fill" />
          <div>
            <p className="text-[9px] text-emerald-300 uppercase font-bold tracking-wider">Declencheur</p>
            <p className="text-[11px] text-white font-medium">{TRIGGER_ICONS[triggerType] || '⚡'} {triggerLabel}</p>
            {triggerEntity && <p className="text-[9px] text-emerald-200/70">{entityLabel}</p>}
          </div>
        </div>

        {/* Arrow */}
        <div className="flex items-center px-1 flex-shrink-0">
          <div className="w-6 h-[2px] bg-gradient-to-r from-[#2D5A4C] to-[#FF8E00]" />
          <CaretDown size={10} className="text-[#FF8E00] -rotate-90 -ml-1" />
        </div>

        {/* Conditions */}
        {condCount > 0 && (
          <>
            <div className="flex items-center gap-2 bg-[#FF8E00]/20 border border-[#FF8E00]/40 rounded-lg px-3 py-2 flex-shrink-0">
              <FunnelSimple size={14} className="text-[#FF8E00]" weight="fill" />
              <div>
                <p className="text-[9px] text-[#FF8E00] uppercase font-bold tracking-wider">Conditions</p>
                <p className="text-[11px] text-white font-medium">{condCount} regle(s) / {conditionGroups.length} groupe(s)</p>
              </div>
            </div>
            <div className="flex items-center px-1 flex-shrink-0">
              <div className="w-6 h-[2px] bg-gradient-to-r from-[#FF8E00] to-[#D47A62]" />
              <CaretDown size={10} className="text-[#D47A62] -rotate-90 -ml-1" />
            </div>
          </>
        )}

        {/* Actions */}
        {actions.filter(a => a.type).map((action, idx) => {
          const color = ACTION_COLORS[action.type] || '#6B7280';
          const label = schema?.actions?.[action.type]?.label || action.type;
          const Icon = ACTION_NODE_ICONS[action.type];
          return (
            <React.Fragment key={action._id || idx}>
              <div className="flex items-center gap-1.5 rounded-lg px-2.5 py-2 flex-shrink-0 border" style={{ background: `${color}15`, borderColor: `${color}40` }}>
                {Icon ? <Icon size={12} style={{ color }} weight="bold" /> : <div className="w-2 h-2 rounded-full" style={{ background: color }} />}
                <p className="text-[10px] text-white font-medium whitespace-nowrap">{label}</p>
              </div>
              {idx < actions.filter(a => a.type).length - 1 && (
                <div className="flex items-center px-0.5 flex-shrink-0">
                  <div className="w-4 h-[2px]" style={{ background: color }} />
                  <CaretDown size={8} style={{ color }} className="-rotate-90 -ml-0.5" />
                </div>
              )}
            </React.Fragment>
          );
        })}

        {actions.filter(a => a.type).length === 0 && (
          <div className="flex items-center gap-1.5 bg-white/5 border border-dashed border-white/20 rounded-lg px-3 py-2 flex-shrink-0">
            <Plus size={12} className="text-white/40" />
            <p className="text-[10px] text-white/40 italic">Ajouter des actions</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ===== Main Builder ===== */
export default function WorkflowBuilder({ schema, initialData, onSave, onCancel, saving }) {
  const [name, setName] = useState(initialData?.name || '');
  const [desc, setDesc] = useState(initialData?.description || '');
  const [triggerType, setTriggerType] = useState(initialData?.trigger?.type || '');
  const [triggerEntity, setTriggerEntity] = useState(initialData?.trigger?.entity_type || '');
  const [triggerFromStatut, setTriggerFromStatut] = useState(initialData?.trigger?.config?.from_statut || '');
  const [triggerToStatut, setTriggerToStatut] = useState(initialData?.trigger?.config?.to_statut || '');
  const [conditionsLogic, setConditionsLogic] = useState('and');
  const [conditionGroups, setConditionGroups] = useState(() => {
    const conds = initialData?.conditions;
    if (conds && typeof conds === 'object' && !Array.isArray(conds)) {
      setConditionsLogic(conds.logic || 'and');
      return conds.groups || [];
    }
    if (Array.isArray(conds) && conds.length > 0) {
      return [{ logic: 'and', rules: conds }];
    }
    return [];
  });
  const [actions, setActions] = useState(() => {
    const acts = initialData?.actions || [];
    return acts.map((a, i) => ({ ...a, _id: `action-${i}-${Date.now()}` }));
  });
  const [showPreview, setShowPreview] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setActions(prev => {
        const oldIndex = prev.findIndex(a => a._id === active.id);
        const newIndex = prev.findIndex(a => a._id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  const addAction = (type = '') => setActions([...actions, { type, config: {}, _id: `action-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }]);
  const removeAction = (idx) => setActions(actions.filter((_, i) => i !== idx));
  const duplicateAction = (idx) => {
    const original = actions[idx];
    const copy = { ...original, config: { ...original.config }, _id: `action-dup-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` };
    const updated = [...actions];
    updated.splice(idx + 1, 0, copy);
    setActions(updated);
  };
  const updateAction = (idx, field, value) => {
    const updated = [...actions];
    if (field === 'type') {
      updated[idx] = { ...updated[idx], type: value, config: {} };
    } else {
      updated[idx] = { ...updated[idx], config: { ...updated[idx].config, [field]: value } };
    }
    setActions(updated);
  };

  const addConditionGroup = () => setConditionGroups([...conditionGroups, { logic: 'and', rules: [{ field: '', operator: 'equals', value: '' }] }]);
  const removeConditionGroup = (gi) => setConditionGroups(conditionGroups.filter((_, i) => i !== gi));
  const duplicateConditionGroup = (gi) => {
    const original = conditionGroups[gi];
    const copy = { logic: original.logic, rules: (original.rules || []).map(r => ({ ...r })) };
    const updated = [...conditionGroups];
    updated.splice(gi + 1, 0, copy);
    setConditionGroups(updated);
  };
  const changeGroupLogic = (gi, logic) => {
    const updated = [...conditionGroups];
    updated[gi] = { ...updated[gi], logic };
    setConditionGroups(updated);
  };
  const addRule = (gi) => {
    const updated = [...conditionGroups];
    updated[gi] = { ...updated[gi], rules: [...(updated[gi].rules || []), { field: '', operator: 'equals', value: '' }] };
    setConditionGroups(updated);
  };
  const removeRule = (gi, ri) => {
    const updated = [...conditionGroups];
    updated[gi] = { ...updated[gi], rules: updated[gi].rules.filter((_, i) => i !== ri) };
    setConditionGroups(updated);
  };
  const updateRule = (gi, ri, field, value) => {
    const updated = [...conditionGroups];
    updated[gi] = { ...updated[gi], rules: updated[gi].rules.map((r, i) => i === ri ? { ...r, [field]: value } : r) };
    setConditionGroups(updated);
  };

  const handleSave = () => {
    const conditions = conditionGroups.length > 0
      ? { logic: conditionsLogic, groups: conditionGroups.map(g => ({ logic: g.logic, rules: (g.rules || []).filter(r => r.field) })) }
      : [];

    onSave({
      name, description: desc,
      trigger: {
        type: triggerType, entity_type: triggerEntity,
        config: triggerType === 'entity_status_change' ? { from_statut: triggerFromStatut || undefined, to_statut: triggerToStatut || undefined } : {},
      },
      conditions,
      actions: actions.filter(a => a.type).map(({ _id, ...rest }) => rest),
    });
  };

  return (
    <div className="space-y-4" data-testid="workflow-builder">
      {/* Name + Description */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-[#5E6D66] mb-1 block font-bold uppercase tracking-wider">Nom</label>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Suivi dossier valide" className="h-9 text-sm" data-testid="builder-name" />
        </div>
        <div>
          <label className="text-[10px] text-[#5E6D66] mb-1 block font-bold uppercase tracking-wider">Description</label>
          <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description optionnelle" className="h-9 text-sm" data-testid="builder-desc" />
        </div>
      </div>

      {/* Flow Preview Toggle + Preview */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-1.5 text-[10px] font-bold text-[#2D5A4C] uppercase tracking-wider hover:text-[#1E4136] transition-colors"
          data-testid="toggle-preview-btn"
        >
          <Eye size={12} weight={showPreview ? 'fill' : 'regular'} />
          Apercu du flux
          <CaretDown size={10} className={`transition-transform ${showPreview ? '' : '-rotate-90'}`} />
        </button>
      </div>
      {showPreview && (
        <FlowPreview
          triggerType={triggerType}
          triggerEntity={triggerEntity}
          conditionGroups={conditionGroups}
          actions={actions}
          schema={schema}
        />
      )}

      {/* Visual Flow */}
      <div className="relative">
        {/* Trigger Block */}
        <Card className="border-[#2D5A4C] bg-[#2D5A4C]/5 shadow-sm" data-testid="builder-trigger-block">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-[#2D5A4C] flex items-center justify-center">
                <Lightning size={14} className="text-white" weight="fill" />
              </div>
              <span className="text-xs font-bold text-[#2D5A4C] uppercase tracking-wider">Declencheur</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase">Evenement</label>
                <Select value={triggerType} onValueChange={v => { setTriggerType(v); setTriggerEntity(''); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid="builder-trigger-type"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {schema?.triggers && Object.entries(schema.triggers).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{TRIGGER_ICONS[k] || '⚡'} {v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase">Entite</label>
                <Select value={triggerEntity} onValueChange={setTriggerEntity}>
                  <SelectTrigger className="h-8 text-xs" data-testid="builder-trigger-entity"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {(schema?.triggers?.[triggerType]?.entities || ['dossier', 'task', 'contact', 'payment']).map(e => (
                      <SelectItem key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {triggerType === 'entity_status_change' && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase">De statut</label>
                  <Input value={triggerFromStatut} onChange={e => setTriggerFromStatut(e.target.value)} placeholder="ex: en_cours" className="h-7 text-xs" data-testid="builder-from-statut" />
                </div>
                <div>
                  <label className="text-[9px] text-[#5E6D66] mb-0.5 block uppercase">Vers statut</label>
                  <Input value={triggerToStatut} onChange={e => setTriggerToStatut(e.target.value)} placeholder="ex: valide" className="h-7 text-xs" data-testid="builder-to-statut" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Connector */}
        <div className="flex justify-center py-1">
          <div className="w-0.5 h-6 bg-gradient-to-b from-[#2D5A4C] to-[#FF8E00]" />
        </div>

        {/* Conditions Block */}
        <Card className="border-[#FF8E00]/30 bg-[#FF8E00]/3 shadow-sm" data-testid="builder-conditions-block">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#FF8E00] flex items-center justify-center">
                  <FunnelSimple size={14} className="text-white" weight="fill" />
                </div>
                <span className="text-xs font-bold text-[#FF8E00] uppercase tracking-wider">Conditions</span>
                {conditionGroups.length > 1 && (
                  <Select value={conditionsLogic} onValueChange={setConditionsLogic}>
                    <SelectTrigger className="h-6 text-[10px] w-20 border-[#FF8E00]/30" data-testid="builder-top-logic">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="and">ET (tous)</SelectItem>
                      <SelectItem value="or">OU (un seul)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-[#FF8E00]" onClick={addConditionGroup} data-testid="add-condition-group-btn">
                <Plus size={10} className="mr-0.5" /> Groupe
              </Button>
            </div>
            {conditionGroups.length === 0 ? (
              <p className="text-[10px] text-[#5E6D66] italic text-center py-2">Aucune condition — le workflow se declenchera toujours</p>
            ) : (
              <div className="space-y-2">
                {conditionGroups.map((g, gi) => (
                  <ConditionGroup key={gi} group={g} groupIndex={gi} operators={schema?.conditions} schema={schema}
                    onUpdateRule={updateRule} onRemoveRule={removeRule} onAddRule={addRule}
                    onChangeLogic={changeGroupLogic} onRemoveGroup={removeConditionGroup} onDuplicate={duplicateConditionGroup} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Connector */}
        <div className="flex justify-center py-1">
          <div className="w-0.5 h-6 bg-gradient-to-b from-[#FF8E00] to-[#D47A62]" />
        </div>

        {/* Actions Block */}
        <Card className="border-[#D47A62]/30 bg-[#D47A62]/3 shadow-sm" data-testid="builder-actions-block">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#D47A62] flex items-center justify-center">
                  <GearSix size={14} className="text-white" weight="fill" />
                </div>
                <span className="text-xs font-bold text-[#D47A62] uppercase tracking-wider">Actions ({actions.length})</span>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="h-6 text-[10px] text-[#D47A62]" onClick={() => addAction()} data-testid="builder-add-action-btn">
                  <Plus size={10} className="mr-0.5" /> Action
                </Button>
              </div>
            </div>

            {/* Quick-add action type buttons */}
            <div className="flex flex-wrap gap-1 mb-3" data-testid="quick-add-actions">
              {['delay', 'webhook_http', 'condition_branch'].map(type => {
                const label = schema?.actions?.[type]?.label || type;
                const Icon = ACTION_NODE_ICONS[type];
                const color = ACTION_COLORS[type];
                return (
                  <button
                    key={type}
                    onClick={() => addAction(type)}
                    className="flex items-center gap-1 px-2 py-1 rounded-md border text-[9px] font-medium hover:shadow-sm transition-all"
                    style={{ borderColor: `${color}40`, color, background: `${color}08` }}
                    data-testid={`quick-add-${type}`}
                  >
                    {Icon && <Icon size={10} weight="bold" />}
                    + {label}
                  </button>
                );
              })}
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={actions.map(a => a._id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {actions.map((action, idx) => (
                    <SortableAction key={action._id} id={action._id} action={action} index={idx}
                      schema={schema} onUpdate={updateAction} onRemove={removeAction} onDuplicate={duplicateAction} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            {actions.length === 0 && (
              <p className="text-[10px] text-[#5E6D66] italic text-center py-2">Ajoutez au moins une action</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Variables help */}
      <div className="bg-[#F9F8F5] rounded-lg p-3 text-[10px] text-[#5E6D66] border border-[#E8E6E1]" data-testid="variables-help">
        <p className="font-bold mb-1">Variables dynamiques (cliquez sur <Code size={10} className="inline text-[#2D5A4C]" /> dans les champs) :</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 font-mono">
          {(schema?.variables || []).slice(0, 8).map(v => (
            <span key={v.key} className="text-[#2D5A4C]">{`{${v.key}}`}</span>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 pt-2 border-t border-[#E8E6E1]">
        <Button variant="outline" onClick={onCancel} data-testid="builder-cancel-btn">Annuler</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="builder-save-btn">
          {saving ? <span className="animate-spin mr-1">...</span> : <Lightning size={14} className="mr-1" />}
          {initialData?.id ? 'Mettre a jour' : 'Creer le workflow'}
        </Button>
      </div>
    </div>
  );
}
